import React, { useEffect, useMemo, useState } from "react";
import { api, eur, formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
         AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Search, X, CheckCircle2, XCircle, Calendar, Power, PowerOff, Zap, Undo2 } from "lucide-react";

const DEPT_LABEL = { bar: "Bar", kitchen: "Cucina", beach: "Spiaggia", other: "Altro" };
const norm = (s) => (s || "").toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();

function fmtWhen(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export default function AdminAvailability() {
  const [prods, setProds] = useState([]);
  const [cats, setCats] = useState([]);
  const [search, setSearch] = useState("");
  const [pending, setPending] = useState(new Set());
  const [confirmBulk, setConfirmBulk] = useState(null);
  const [quickFilter, setQuickFilter] = useState("all");
  const [compact, setCompact] = useState(() => localStorage.getItem("sarabi:avail:compact") === "1");

  const load = async () => {
    const [p, c] = await Promise.all([api.get("/admin/products"), api.get("/admin/categories")]);
    setProds(p.data); setCats(c.data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { localStorage.setItem("sarabi:avail:compact", compact ? "1" : "0"); }, [compact]);

  const catById = useMemo(() => {
    const m = {}; cats.forEach((c) => { m[c.id] = c; }); return m;
  }, [cats]);

  const filtered = useMemo(() => {
    const q = norm(search);
    let list = prods;
    // Quick filter
    if (quickFilter === "available") list = list.filter((p) => p.effective_available);
    else if (quickFilter === "unavailable") list = list.filter((p) => !p.effective_available);
    else if (quickFilter === "modifiers") list = list.filter((p) => (p.modifier_group_ids || []).length > 0);
    else if (quickFilter === "scheduled") list = list.filter((p) => p.available_from_at || p.unavailable_until_at);
    else if (quickFilter === "vat-other") list = list.filter((p) => p.vat_rate !== 10);
    if (!q) return list;
    const terms = q.split(/\s+/).filter(Boolean);
    return list.filter((p) => {
      const hay = norm(`${p.name} ${p.description || ""} ${(p.tags || []).join(" ")} ${catById[p.category_id]?.name || ""}`);
      return terms.every((t) => hay.includes(t));
    });
  }, [prods, search, catById, quickFilter]);

  const byDept = useMemo(() => {
    const groups = { bar: [], kitchen: [], beach: [], other: [] };
    filtered.forEach((p) => {
      const dept = p.department || catById[p.category_id]?.department || "other";
      (groups[dept] || groups.other).push(p);
    });
    return groups;
  }, [filtered, catById]);

  const catsByDept = useMemo(() => {
    const groups = { bar: [], kitchen: [], beach: [], other: [] };
    cats.forEach((c) => (groups[c.department || "other"] || groups.other).push(c));
    return groups;
  }, [cats]);

  const toggle = async (p) => {
    if (pending.has(p.id)) return;
    const next = !p.effective_available;
    setProds((cur) => cur.map((x) => x.id === p.id ? { ...x, available: next, effective_available: next,
      available_from_at: next ? null : x.available_from_at,
      unavailable_until_at: next ? null : x.unavailable_until_at } : x));
    setPending((s) => new Set(s).add(p.id));
    try {
      if (next && (p.available_from_at || p.unavailable_until_at)) {
        await api.patch(`/admin/products/${p.id}/schedule`, { available_from_at: null, unavailable_until_at: null });
      }
      await api.patch(`/admin/products/${p.id}/availability?available=${next}`);
      toast.success(`${p.name} → ${next ? "Disponibile" : "Esaurito"}`, { duration: 1200 });
    } catch (e) {
      toast.error(formatApiError(e));
      setProds((cur) => cur.map((x) => x.id === p.id ? { ...x, available: !next, effective_available: !next } : x));
    } finally {
      setPending((s) => { const n = new Set(s); n.delete(p.id); return n; });
    }
  };

  const undo = async (p) => {
    try {
      const r = await api.post(`/admin/products/${p.id}/undo-availability`);
      toast.success(`${p.name} ripristinato a ${r.data.reverted_to ? "Disponibile" : "Esaurito"}`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const runBulk = async () => {
    if (!confirmBulk) return;
    const { filter, filterLabel, value } = confirmBulk;
    try {
      const r = await api.post("/admin/products/bulk-availability", { ...filter, available: value });
      toast.success(`${r.data.modified} prodotti aggiornati (${filterLabel} → ${value ? "Disponibile" : "Esaurito"})`);
      setConfirmBulk(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const totalOn = prods.filter((p) => p.effective_available).length;
  const totalOff = prods.length - totalOn;

  return (
    <div>
      <div className="eyebrow">Gestione</div>
      <h1 className="text-4xl mt-1">Disponibilità Rapida</h1>
      <p className="text-sm text-ink-500 mt-1">Un tap per attivare/disattivare un prodotto. Nessun popup, nessun salvataggio.</p>

      <div className="mt-4 flex items-center gap-3 flex-wrap">
        <div className="chip bg-emerald-50 text-emerald-800 border-emerald-200" data-testid="stat-on"><CheckCircle2 className="w-3 h-3 mr-1 inline" />Disponibili: <b className="ml-1">{totalOn}</b></div>
        <div className="chip bg-red-50 text-red-800 border-red-200" data-testid="stat-off"><XCircle className="w-3 h-3 mr-1 inline" />Esauriti: <b className="ml-1">{totalOff}</b></div>
      </div>

      {/* Azioni massive per reparto */}
      <div className="mt-4 card-elevated p-3 bg-sand-50/70" data-testid="bulk-actions-panel">
        <div className="flex items-center gap-2 mb-2">
          <Zap className="w-4 h-4 text-bronze-600" />
          <b className="text-sm">Azioni massive per reparto</b>
        </div>
        <div className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-4">
          {["bar", "kitchen", "beach", "other"].map((dept) => (
            <div key={dept} className="flex items-center justify-between gap-2 bg-white rounded-xl border border-sand-300 px-3 py-2">
              <b className="text-sm">{DEPT_LABEL[dept]}</b>
              <div className="flex items-center gap-1">
                <button type="button"
                        onClick={() => setConfirmBulk({ filter: { department: dept }, filterLabel: DEPT_LABEL[dept], value: true })}
                        data-testid={`bulk-dept-on-${dept}`}
                        className="text-[11px] rounded-full border border-emerald-500 text-emerald-700 hover:bg-emerald-50 px-2 py-1">
                  <Power className="w-3 h-3 inline mr-0.5" />Disponibile tutto
                </button>
                <button type="button"
                        onClick={() => setConfirmBulk({ filter: { department: dept }, filterLabel: DEPT_LABEL[dept], value: false })}
                        data-testid={`bulk-dept-off-${dept}`}
                        className="text-[11px] rounded-full border border-red-500 text-red-700 hover:bg-red-50 px-2 py-1">
                  <PowerOff className="w-3 h-3 inline mr-0.5" />Esaurisci tutto
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-4 sticky top-0 z-10 bg-sand-50 pt-2 pb-3">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink-500 pointer-events-none" />
          <Input
            data-testid="avail-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca prodotto, tag, categoria… (premi / per focus rapido)"
            className="pl-9 pr-9 rounded-full bg-white border-sand-300 h-11"
          />
          {search && (
            <button data-testid="avail-search-clear" onClick={() => setSearch("")}
                    aria-label="Pulisci ricerca"
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full hover:bg-sand-200 flex items-center justify-center text-ink-500">
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="mt-2 flex items-center gap-1.5 flex-wrap" data-testid="quick-filters">
          {[["all", "Tutti"], ["available", "Disponibili"], ["unavailable", "Esauriti"], ["modifiers", "Con modificatori"], ["scheduled", "Programmati"], ["vat-other", "IVA ≠ 10%"]].map(([k, l]) => (
            <button key={k} onClick={() => setQuickFilter(k)} data-testid={`qfilter-${k}`}
                    className={`text-xs rounded-full px-3 py-1 border ${quickFilter === k ? "bg-bronze-600 text-white border-bronze-600" : "bg-white border-sand-300 hover:bg-sand-100"}`}>
              {l}
            </button>
          ))}
          <div className="ml-auto flex items-center gap-1.5">
            <span className="text-[11px] text-ink-500">Modalità:</span>
            <button onClick={() => setCompact(false)} data-testid="mode-normal"
                    className={`text-xs rounded-full px-3 py-1 border ${!compact ? "bg-bronze-600 text-white border-bronze-600" : "bg-white border-sand-300"}`}>Normale</button>
            <button onClick={() => setCompact(true)} data-testid="mode-compact"
                    className={`text-xs rounded-full px-3 py-1 border ${compact ? "bg-bronze-600 text-white border-bronze-600" : "bg-white border-sand-300"}`}>Operatore</button>
          </div>
        </div>
      </div>

      <div className="space-y-6 mt-2">
        {["bar", "kitchen", "beach", "other"].map((dept) => {
          const items = byDept[dept] || [];
          if (items.length === 0) return null;
          return (
            <section key={dept} data-testid={`dept-block-${dept}`}>
              <h2 className="font-serif text-2xl mb-2">{DEPT_LABEL[dept]} <span className="text-sm text-ink-500 font-sans">({items.length})</span></h2>

              {/* Azioni per categoria dentro il reparto */}
              {(catsByDept[dept] || []).filter((c) => (byDept[dept] || []).some((p) => p.category_id === c.id)).length > 0 && (
                <div className="mb-2 flex flex-wrap gap-1.5">
                  {(catsByDept[dept] || []).filter((c) => (byDept[dept] || []).some((p) => p.category_id === c.id)).map((c) => (
                    <div key={c.id} className="inline-flex items-center gap-1 rounded-full border border-sand-300 bg-white px-2 py-0.5">
                      <span className="text-[11px] text-ink-500">{c.name}</span>
                      <button type="button"
                              onClick={() => setConfirmBulk({ filter: { category_id: c.id }, filterLabel: `categoria ${c.name}`, value: true })}
                              data-testid={`bulk-cat-on-${c.id}`}
                              className="text-[10px] text-emerald-700 hover:underline">Tutti ok</button>
                      <span className="text-[10px] text-ink-500">·</span>
                      <button type="button"
                              onClick={() => setConfirmBulk({ filter: { category_id: c.id }, filterLabel: `categoria ${c.name}`, value: false })}
                              data-testid={`bulk-cat-off-${c.id}`}
                              className="text-[10px] text-red-700 hover:underline">Esaurisci</button>
                    </div>
                  ))}
                </div>
              )}

              <div className={compact ? "grid gap-1.5 grid-cols-1 lg:grid-cols-2 xl:grid-cols-3" : "grid gap-2 sm:grid-cols-2 lg:grid-cols-3"}>
                {items.map((p) => (
                  <AvailabilityRow key={p.id} p={p} cat={catById[p.category_id]} onToggle={toggle} onUndo={undo} pending={pending.has(p.id)} compact={compact} />
                ))}
              </div>
            </section>
          );
        })}
        {filtered.length === 0 && (
          <div className="card-elevated p-8 text-center text-ink-500">Nessun prodotto corrisponde a "{search}"</div>
        )}
      </div>

      <AlertDialog open={!!confirmBulk} onOpenChange={(o) => !o && setConfirmBulk(null)}>
        <AlertDialogContent data-testid="bulk-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confermi l'azione massiva?</AlertDialogTitle>
            <AlertDialogDescription>
              {confirmBulk && (
                <>Stai per <b>{confirmBulk.value ? "rendere DISPONIBILI" : "rendere NON DISPONIBILI"}</b> tutti i prodotti di <b>{confirmBulk.filterLabel}</b>. L'operazione può essere annullata solo riportandoli manualmente.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bulk-cancel">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={runBulk} data-testid="bulk-confirm-ok">Conferma</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function AvailabilityRow({ p, cat, onToggle, pending, onUndo, compact }) {
  const on = p.effective_available;
  const scheduled = p.available_from_at || p.unavailable_until_at;
  const whenText = fmtWhen(p.available_from_at || p.unavailable_until_at);
  if (compact) {
    return (
      <div className={`w-full rounded-lg border flex items-center gap-2 px-2 py-1.5 text-sm ${on ? "bg-emerald-50 border-emerald-400" : "bg-red-50 border-red-400"}`} data-testid={`avail-card-${p.id}`}>
        <button onClick={() => onToggle(p)} disabled={pending} data-testid={`avail-toggle-${p.id}`}
                className={`w-6 h-6 shrink-0 rounded-full text-white text-[10px] font-bold ${on ? "bg-emerald-600" : "bg-red-600"}`}
                data-testid-badge={`avail-badge-${p.id}`}>{on ? "OK" : "OFF"}</button>
        <div className="flex-1 min-w-0 truncate">
          <b className="truncate">{p.name}</b>
          <span className="ml-2 text-[11px] text-ink-500">{cat?.name || "—"} · {eur(p.price_cents)}</span>
        </div>
        <button onClick={() => onUndo(p)} data-testid={`avail-undo-${p.id}`} className="p-1 rounded hover:bg-white text-ink-500" title="Ripristina"><Undo2 className="w-3.5 h-3.5" /></button>
      </div>
    );
  }
  return (
    <div className={`w-full rounded-2xl border-2 flex items-center gap-3 transition-all hover:shadow-md ${on ? "bg-emerald-50 border-emerald-500" : "bg-red-50 border-red-500"}`}
         data-testid={`avail-card-${p.id}`}>
      <button onClick={() => onToggle(p)} disabled={pending} data-testid={`avail-toggle-${p.id}`}
        className="flex-1 text-left px-4 py-3 flex items-center gap-3 active:scale-[0.98] disabled:opacity-60">
        <div className={`w-14 h-14 shrink-0 rounded-full flex items-center justify-center text-white font-bold text-xs ${on ? "bg-emerald-600" : "bg-red-600"}`}
             data-testid={`avail-badge-${p.id}`}>{on ? "OK" : "OFF"}</div>
        <div className="min-w-0 flex-1">
          <div className="font-medium truncate">{p.name}</div>
          <div className="text-xs text-ink-500 truncate">{cat?.name || "—"} · {eur(p.price_cents)}</div>
          {scheduled && whenText && (
            <div className="mt-1 text-[11px] flex items-center gap-1 text-bronze-700">
              <Calendar className="w-3 h-3" />
              {p.available_from_at ? `Disponibile dal ${whenText}` : `Esaurito fino al ${whenText}`}
            </div>
          )}
        </div>
        <div className={`text-xs font-bold uppercase tracking-wider ${on ? "text-emerald-700" : "text-red-700"}`}>{on ? "Disponibile" : "Esaurito"}</div>
      </button>
      <button onClick={(e) => { e.stopPropagation(); onUndo(p); }} data-testid={`avail-undo-${p.id}`}
              className="mr-3 p-1.5 rounded-full text-ink-500 hover:bg-white hover:text-bronze-700 shrink-0" title="Ripristina ultimo stato manuale">
        <Undo2 className="w-4 h-4" />
      </button>
    </div>
  );
}
